<script setup lang="ts">
/**
 * Appointment of DPO (B32116) — Directors' Resolutions in Writing + Letter of Authorisation (한 문서, 2쪽).
 *   원본: Appointment of DPO.docx (…/SG/프리셋/dpo/_lettaga_templatized)
 *   토큰: company_name / company_registration_no / registered_office_address · [[if is_sole_director]](이사 1명) ·
 *         dpo_name · [[if dpo_gender == "F"]]her[[else]]his · [[Signers]](이사 전원 → dr_Signers) · resolution_date ·
 *         authorisation_date · dpo_designation · dpo_contact_no · dpo_email · lodgement_date · company_mainline ·
 *         [[dpo_signature]](DPO 본인 — 패널이 dpo_name 으로 만든다)
 */
import { computed } from 'vue';
import CddVarField from '../final-dividends/cdd/CddVarField.vue';
import AgmSigners from '../final-dividends/cdd/AgmSigners.vue';
import { useAgmFill } from '../final-dividends/cdd/useCddFill';
import '../final-dividends/docs/agmDoc.css';

const props = withDefaults(defineProps<{ soleDirector?: boolean; gender?: string }>(), { soleDirector: false, gender: 'M' });
const emit = defineEmits<{ (e: 'update:gender', v: string): void }>();
const fill = useAgmFill();
const isFemale = computed(() => props.gender === 'F');
const dpoName = computed(() => String(fill.values.dpo_name ?? '').trim());
const dpoDesignation = computed(() => String(fill.values.dpo_designation ?? '').trim());

const DUTIES = [
  'to ensure compliance of PDPA when developing and implementing policies and processes for handling personal data;',
  'to foster a data protection culture among employees and communicate personal data protection policies to stakeholders;',
  'to manage personal data protection related queries and complaints;',
  'to alert management to any risks that might arise with regard to personal data; and',
  'to liaise with the Personal Data Protection Commission (PDPC) on data protection matters, if necessary.',
];
const LETTERS = ['a', 'b', 'c', 'd', 'e'];
</script>

<template>
  <div class="agm-doc dpo-doc">
    <!-- ── Directors' Resolutions in Writing ── -->
    <div class="doc-head">
      <div><b><CddVarField field-key="company_name" label="Company Name" /></b></div>
      <div>(INCORPORATED IN SINGAPORE)</div>
      <div>Company Registration No. <CddVarField field-key="company_registration_no" label="Company Registration No." /></div>
      <div>(the &ldquo;Company&rdquo;)</div>
    </div>

    <div class="doc-title dpo-title">
      {{ soleDirector ? 'SOLE DIRECTOR’S' : 'DIRECTORS’' }} RESOLUTIONS IN WRITING PASSED PURSUANT TO THE CONSTITUTION OF THE COMPANY
    </div>
    <div class="dpo-sub">APPOINTMENT OF DATA PROTECTION OFFICER</div>
    <div class="dpo-resolved">RESOLVED:</div>

    <div class="dpo-item">
      <span class="no">1.</span>
      <div>
        <p class="doc-p">
          That <b><CddVarField field-key="dpo_name" label="Data Protection Officer" /></b>, be and is hereby appointed as the
          Data Protection Officer (&ldquo;DPO&rdquo;) pursuant to the Personal Data Protection Act 2012 (PDPA) with effect from
          <span class="doc-toggle" title="Click to switch his / her" @click="emit('update:gender', isFemale ? 'M' : 'F')">{{ isFemale ? 'her' : 'his' }}</span>
          date of consent:
        </p>
        <div v-for="(d, i) in DUTIES" :key="i" class="dpo-sub-item">
          <span class="no">{{ LETTERS[i] }}.</span><span>{{ d }}</span>
        </div>
      </div>
    </div>

    <div class="dpo-item">
      <span class="no">2.</span>
      <p class="doc-p">
        That any of the officers or such other prescribed person as directed by
        {{ soleDirector ? 'the Sole Director' : 'all the Directors' }} be and is hereby authorised to e-file the relevant
        return relating thereto with the Accounting and Corporate Regulatory Authority for and on behalf of the Company.
      </p>
    </div>

    <div class="sign-block">
      <AgmSigners var-key="dr_Signers" board-label="Director" />
    </div>
    <p class="doc-p">Dated: <CddVarField field-key="resolution_date" label="Resolution date" /></p>

    <!-- ── Letter of Authorisation (2쪽) ── -->
    <div class="dpo-page-break" aria-hidden="true">Page 2 — Letter of Authorisation</div>

    <p class="doc-p">DATE: <CddVarField field-key="authorisation_date" label="Letter date" /></p>
    <div class="dpo-addr">
      <div>THE SECRETARY</div>
      <div><CddVarField field-key="company_name" label="Company Name" /></div>
      <div><CddVarField field-key="registered_office_address" label="Registered Office Address" /></div>
    </div>
    <p class="doc-p">Dear Sirs / Madam,</p>
    <div class="dpo-sub">LETTER OF AUTHORISATION</div>
    <p class="doc-p">
      I hereby give my authorization to lodge my appointment as the Data Protection Officer of the Company and provide the
      following information:
    </p>
    <table class="dpo-info">
      <tbody>
        <tr><td>Full Name</td><td>:</td><td><CddVarField field-key="dpo_name" label="Data Protection Officer" /></td></tr>
        <tr><td>Designation</td><td>:</td><td><CddVarField field-key="dpo_designation" label="Designation" /></td></tr>
        <tr><td>Contact Number</td><td>:</td><td><CddVarField field-key="dpo_contact_no" label="Contact Number" /></td></tr>
        <tr><td>Business Email address</td><td>:</td><td><CddVarField field-key="dpo_email" label="Business Email" /></td></tr>
        <tr><td>Lodgement Date</td><td>:</td><td><CddVarField field-key="lodgement_date" label="Lodgement Date" /></td></tr>
        <tr><td>Company Mainline</td><td>:</td><td><CddVarField field-key="company_mainline" label="Company Mainline" /></td></tr>
      </tbody>
    </table>
    <p class="doc-p">Yours faithfully</p>
    <!-- [[dpo_signature]] — DPO 본인 서명(위 DPO 이름으로 자동) -->
    <div class="dpo-sign">
      <div class="line" />
      <div class="nm">{{ dpoName || 'Data Protection Officer' }}</div>
      <div v-if="dpoDesignation" class="tt">{{ dpoDesignation }}</div>
    </div>
  </div>
</template>

<style scoped>
.dpo-title { margin-bottom: 10px; }
.dpo-sub { font-weight: 700; margin: 14px 0 10px; }
.dpo-resolved { margin-bottom: 8px; }
.dpo-item { display: grid; grid-template-columns: 26px 1fr; gap: 4px; margin-bottom: 10px; }
.dpo-sub-item { display: grid; grid-template-columns: 22px 1fr; gap: 4px; margin: 0 0 6px 6px; }
.no { font-weight: 400; }
.dpo-page-break { margin: 28px -32px 22px; padding: 6px 32px; border-top: 1px dashed #c3cad6; background: #f6f8fb; color: #8a95a8; font-size: 10.5px; font-weight: 700; }
.dpo-addr { margin: 10px 0 14px; line-height: 1.6; }
.dpo-info { border-collapse: collapse; margin: 6px 0 14px; }
.dpo-info td { padding: 3px 8px 3px 0; vertical-align: top; }
.dpo-info td:first-child { min-width: 170px; }
.dpo-sign { width: 220px; margin-top: 34px; text-align: center; }
.dpo-sign .line { border-bottom: 1px solid #111; height: 34px; margin-bottom: 4px; }
.dpo-sign .nm { font-weight: 700; }
.dpo-sign .tt { color: #42506a; }
</style>
